import { Bell } from "lucide-react";
import { motion } from "framer-motion";
import { isHighRiskSeverity, normalizeSeverity } from "./alertSeverityUtils";

function bellRingKeyframes(severity) {
  const key = normalizeSeverity(severity);

  if (key === "critical") {
    return [0, 22, -22, 18, -18, 12, -12, 6, -6, 0];
  }

  if (key === "high") {
    return [0, 16, -16, 11, -11, 6, -6, 0];
  }

  if (key === "medium") {
    return [0, 11, -11, 7, -7, 3, -3, 0];
  }

  return [0, 8, -8, 5, -5, 2, -2, 0];
}

function bellRingTiming(severity) {
  const key = normalizeSeverity(severity);

  if (key === "critical") {
    return { duration: 0.55, repeatDelay: 0.9 };
  }

  if (key === "high") {
    return { duration: 0.75, repeatDelay: 1.8 };
  }

  if (key === "medium") {
    return { duration: 0.95, repeatDelay: 3 };
  }

  return { duration: 1.1, repeatDelay: 4.5 };
}

export function AlertsBellIcon({
  unreadCount = 0,
  maxUnreadSeverity = null,
  size = 18,
  density = "normal",
  className = "",
}) {
  const hasUnread = unreadCount > 0;
  const severity = hasUnread ? normalizeSeverity(maxUnreadSeverity) : null;
  const ringTiming = bellRingTiming(severity);
  const iconSize = density === "iconOnly" ? 18 : size;

  return (
    <span className={`relative inline-flex ${className}`}>
      <motion.span
        className="inline-flex"
        style={{ transformOrigin: "50% 0%" }}
        animate={
          hasUnread
            ? { rotate: bellRingKeyframes(severity) }
            : { rotate: 0 }
        }
        transition={
          hasUnread
            ? {
                ...ringTiming,
                repeat: Infinity,
                ease: "easeInOut",
              }
            : { duration: 0.2 }
        }
      >
        <Bell size={iconSize} />
      </motion.span>
    </span>
  );
}

function unreadTone({ hasUnread, isCritical, isHighRisk, isDarkMode, variant }) {
  if (!hasUnread) {
    if (variant === "nav") {
      return isDarkMode
        ? "text-gray-100 hover:bg-gray-700"
        : "text-black hover:bg-blue-200";
    }

    return isDarkMode
      ? "bg-gray-700 text-gray-100 hover:bg-gray-600"
      : "bg-gray-100 text-gray-900 hover:bg-gray-200";
  }

  if (isCritical) {
    return variant === "nav"
      ? isDarkMode
        ? "bg-red-500/20 text-red-300 hover:bg-red-500/30 ring-1 ring-inset ring-red-500/40"
        : "bg-red-50 text-red-700 hover:bg-red-100 ring-1 ring-inset ring-red-300/70"
      : isDarkMode
        ? "bg-red-500/20 text-red-300 hover:bg-red-500/30 ring-2 ring-red-500/50"
        : "bg-red-50 text-red-700 hover:bg-red-100 ring-2 ring-red-400/60";
  }

  if (isHighRisk) {
    return variant === "nav"
      ? isDarkMode
        ? "bg-orange-500/15 text-orange-300 hover:bg-orange-500/25 ring-1 ring-inset ring-orange-500/35"
        : "bg-orange-50 text-orange-700 hover:bg-orange-100 ring-1 ring-inset ring-orange-300/60"
      : isDarkMode
        ? "bg-orange-500/15 text-orange-300 hover:bg-orange-500/25 ring-1 ring-orange-500/40"
        : "bg-orange-50 text-orange-700 hover:bg-orange-100 ring-1 ring-orange-300/70";
  }

  return variant === "nav"
    ? isDarkMode
      ? "bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25"
      : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
    : isDarkMode
      ? "bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25"
      : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100";
}

function BellGlyph({ hasUnread, severity, iconSize, ringTiming }) {
  return (
    <motion.span
      className="relative inline-flex"
      style={{ transformOrigin: "50% 0%" }}
      animate={
        hasUnread
          ? { rotate: bellRingKeyframes(severity) }
          : { rotate: 0 }
      }
      transition={
        hasUnread
          ? {
              ...ringTiming,
              repeat: Infinity,
              ease: "easeInOut",
            }
          : { duration: 0.2 }
      }
    >
      <Bell size={iconSize} style={{ minWidth: `${iconSize}px` }} />
    </motion.span>
  );
}

export default function AlertsBellButton({
  unreadCount = 0,
  maxUnreadSeverity = null,
  onClick,
  size = 20,
  iconSize,
  isDarkMode = false,
  className = "",
  showCountBadge = false,
  density = "normal",
  variant = "icon",
  showLabel = false,
  label = "Alerts",
  navItemPad = "",
  navItemText = "",
}) {
  const hasUnread = unreadCount > 0;
  const severity = hasUnread ? normalizeSeverity(maxUnreadSeverity) : null;
  const isCritical = severity === "critical";
  const isHighRisk = isHighRiskSeverity(severity);
  const ringTiming = bellRingTiming(severity);
  const resolvedIconSize = iconSize ?? (density === "iconOnly" ? 18 : size);
  const buttonTone = unreadTone({ hasUnread, isCritical, isHighRisk, isDarkMode, variant });

  const bellContent = (
    <>
      {hasUnread && isCritical && variant === "icon" && (
        <motion.span
          className="pointer-events-none absolute inset-0 rounded-full"
          animate={{
            boxShadow: [
              "0 0 0 0 rgba(239, 68, 68, 0.45)",
              "0 0 0 8px rgba(239, 68, 68, 0)",
              "0 0 0 0 rgba(239, 68, 68, 0)",
            ],
          }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeOut" }}
        />
      )}

      <span className="relative inline-flex shrink-0" style={{ minWidth: `${resolvedIconSize}px` }}>
        <BellGlyph
          hasUnread={hasUnread}
          severity={severity}
          iconSize={resolvedIconSize}
          ringTiming={ringTiming}
        />

        {hasUnread && !showCountBadge && (
          <motion.span
            className={`absolute rounded-full ${
              variant === "nav" ? "-right-0.5 -top-0.5" : "right-1 top-1"
            } ${
              isCritical ? "h-2.5 w-2.5 bg-red-500" : isHighRisk ? "h-2.5 w-2.5 bg-orange-500" : "h-2 w-2 bg-red-500"
            }`}
            animate={
              isHighRisk
                ? { scale: [1, 1.35, 1], opacity: [1, 0.75, 1] }
                : { scale: [1, 1.15, 1] }
            }
            transition={{
              duration: isCritical ? 0.8 : 1.2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        )}
      </span>

      {showLabel && (
        <span className="ml-3 whitespace-nowrap">{label}</span>
      )}

      {hasUnread && showCountBadge && (
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold text-white ${
            isCritical ? "bg-red-600" : isHighRisk ? "bg-orange-500" : "bg-red-500"
          }`}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </>
  );

  if (variant === "nav") {
    return (
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
        className={`relative flex w-full items-center ${navItemPad} ${navItemText} font-medium rounded-lg transition-colors ${buttonTone} ${className}`}
        aria-label={hasUnread ? `Open alerts, ${unreadCount} unread` : "Open alerts"}
      >
        {bellContent}
      </motion.button>
    );
  }

  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`relative rounded-full p-2 transition-colors ${buttonTone} ${className}`}
      aria-label={hasUnread ? `Open alerts, ${unreadCount} unread` : "Open alerts"}
    >
      {bellContent}
    </motion.button>
  );
}
